import ArrowRight16 from '@carbon/icons-react/es/arrow--right/16';
import BuildingInsights_132 from '@carbon/icons-react/es/building--insights-1/32';
import Information16 from '@carbon/icons-react/es/information/16';
import Save16 from '@carbon/icons-react/es/save/16';
import { Col, Row, Tooltip } from 'antd';
import { isEmpty } from 'lodash';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { AccountStatus } from 'api/account';
import { CompanyAPI } from 'api/company';
import { CompanyType, UserType } from 'api/models';
import { PartnerCompany } from 'api/partner/types';
import { Button } from 'components/atoms/button';
import {
  CustomLayoutDrawer,
  CustomLayoutDrawerContent,
  DefaultCustomLayoutDrawerProps,
} from 'components/atoms/drawer';
import { Form } from 'components/atoms/form';
import { Input, InputNumber } from 'components/atoms/input';
import { Select } from 'components/atoms/select';
import { DescriptiveOption } from 'components/atoms/select/custom-components';
import { ImageUpload } from 'components/molecules/image-upload';
import { toastService } from 'components/molecules/toast';
import { useAccount, useBillingFeatureFlag } from 'hooks/use-account';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useAvailableLicensesQuery } from 'queries/billing';
import { useUsersQuery } from 'queries/members';
import {
  useCreatePartnerCompanyMutation,
  useGetAllPartnerPermissions,
  useUpdatePartnerCompanyMutation,
} from 'queries/partner';
import { isValidEmail } from 'utils/is-valid-email';
import { URlRegEx } from 'utils/urls';
import {
  translationKeyForAccessLevelDescription,
  translationKeyForAccessLevelLabel,
} from '../utils';
import {
  Checkbox,
  DrawerContent,
  Footer,
  FormWrapper,
  LabelWrapper,
  CentreAlignWrapper,
} from './component';

const MIN_SEATS = 1;

interface Props {
  partner?: PartnerCompany;
  visible: boolean;
  onClose: () => void;
  onPartnerCreate?: (partner: PartnerCompany) => void;
}
export const NewPartnerDrawer: FC<Props> = ({
  partner,
  visible,
  onClose,
  onPartnerCreate,
}) => {
  const translate = useTranslate();
  const buttonText = `${useTranslation('upload_logo')}...`;
  const helpText = useTranslation('project.settings.image.help.recommended');
  const requiredErrorMessage = useTranslation('input.required');
  const emailErrorMessage = useTranslation('input.email');
  const { data: partnerPermissions, isLoading: isPartnerPermissionsLoading } =
    useGetAllPartnerPermissions();
  const isBillingEnabled = useBillingFeatureFlag();
  const { data: users } = useUsersQuery();
  const {
    user: { company: currentUserCompany, userType: currentUserType },
  } = useUser();

  const accessLevelOptions = useMemo(
    () =>
      (partnerPermissions ?? []).map(
        ({
          partnerPermissionSetId: value,
          partnerPermissionSetName: label,
        }) => ({
          label: translate(translationKeyForAccessLevelLabel(label)),
          value,
          description: translate(
            translationKeyForAccessLevelDescription(label),
          ),
        }),
      ),
    [partnerPermissions, translate],
  );
  const isEdit = partner != null;
  const [form] = Form.useForm();
  const { mutate: createPartner, isLoading: isCreating } =
    useCreatePartnerCompanyMutation();
  const { mutate: updatePartner, isLoading: isUpdating } =
    useUpdatePartnerCompanyMutation();
  const { status: accountStatus } = useAccount();
  const availableLicenses = useAvailableLicensesQuery();
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string>();
  const [isLogoUploading, setLogoUploadingStatus] = useState(false);
  const uploadLogo = useCallback(({ file }) => {
    setLogoUploadingStatus(true);
    CompanyAPI.uploadPicture(file)
      .then((res) => {
        setCompanyLogoUrl(res.location);
      })
      .finally(() => {
        setLogoUploadingStatus(false);
      });
  }, []);

  useEffect(() => {
    if (isEdit) {
      setCompanyLogoUrl(partner.companyLogoUrl);
      form.setFieldsValue({
        companyName: partner.companyName,
        companyUrl: partner.companyUrl,
        adminEmail: partner.admins?.[0]?.emailId,
        maxSeat: partner.maxSeat ?? MIN_SEATS,
        accessLevel: accessLevelOptions.find(
          (x) => x.value === partner.accessLevel.partnerPermissionSetId,
        ),
        maskPartner: !partner.strategic,
      });
    } else {
      form.setFieldsValue({
        maxSeat: MIN_SEATS,
        maskPartner: false,
      });
    }
  }, [
    accessLevelOptions,
    form,
    isEdit,
    partner?.accessLevel?.partnerPermissionSetId,
    partner?.admins,
    partner?.companyLogoUrl,
    partner?.companyName,
    partner?.companyUrl,
    partner?.maxSeat,
    partner?.strategic,
  ]);

  const submitForm = useCallback(() => {
    form.validateFields().then((values) => {
      const {
        companyName,
        companyUrl,
        adminEmail,
        maxSeat,
        accessLevel,
        maskPartner,
      } = values;

      if (isEdit) {
        updatePartner(
          {
            ...partner,
            companyName,
            companyUrl,
            companyLogoUrl,
            strategic: !maskPartner,
            maxSeat,
            accessLevel:
              (partnerPermissions ?? []).find(
                (x) => x.partnerPermissionSetId === accessLevel.value,
              ) ?? partner.accessLevel,
          },
          {
            onSuccess: () => {
              onClose();
              toastService({
                title: translate('partners.new_drawer.update.success.title'),
                message: translate(
                  'partners.new_drawer.update.success.message',
                ),
              }).success();
            },
            onError: () => {
              toastService({
                title: translate('partners.new_drawer.update.failure.title'),
                message: translate(
                  'partners.new_drawer.update.failure.message',
                ),
              }).error();
            },
          },
        );
      } else {
        createPartner(
          {
            companyName,
            companyUrl,
            companyType: CompanyType.Partner,
            companyLogoUrl,
            admins: [{ emailId: adminEmail }],
            maxSeat,
            accessLevel: {
              partnerPermissionSetId: accessLevel.value,
            },
            capacity: 40,
            active: true,
            strategic: !maskPartner,
          },
          {
            onSuccess: (newPartner) => {
              onPartnerCreate?.(newPartner);
              onClose();
              toastService({
                title: translate('partners.new_drawer.create.success.title'),
                message: translate(
                  'partners.new_drawer.create.success.message',
                  { companyName, b: (chunks) => <strong>{chunks}</strong> },
                ),
              }).success();
            },
            onError: () => {
              toastService({
                title: translate('partners.new_drawer.create.failure.title'),
                message: translate(
                  'partners.new_drawer.create.failure.message',
                ),
              }).error();
            },
          },
        );
      }
    });
  }, [
    companyLogoUrl,
    createPartner,
    form,
    isEdit,
    onClose,
    onPartnerCreate,
    partner,
    partnerPermissions,
    translate,
    updatePartner,
  ]);

  return (
    <CustomLayoutDrawer
      {...DefaultCustomLayoutDrawerProps}
      width={680}
      visible={visible}
      onClose={onClose}
    >
      <DrawerContent>
        <CustomLayoutDrawerContent.Title>
          {isEdit ? (
            <FormattedMessage id="partners.edit.drawer.title" />
          ) : (
            <FormattedMessage id="partners.new.drawer.title" />
          )}
        </CustomLayoutDrawerContent.Title>
        <CustomLayoutDrawerContent.Content>
          <ImageUpload
            onUpload={uploadLogo}
            isLoading={isLogoUploading}
            buttonText={buttonText}
            help={helpText}
            src={companyLogoUrl}
            showSizeRestriction
            showFormatRestriction
            // eslint-disable-next-line react/jsx-pascal-case
            placeholderIcon={<BuildingInsights_132 />}
          />
          <FormWrapper>
            <Form
              layout="vertical"
              initialValues={{
                accessLevel: accessLevelOptions[0],
              }}
              form={form}
            >
              <Form.Item
                label={useTranslation('name')}
                required
                rules={[
                  {
                    required: true,
                    message: requiredErrorMessage,
                  },
                ]}
                name="companyName"
              >
                <Input
                  placeholder={useTranslation(
                    'partners.new.drawer.form.name.placeholder',
                  )}
                  autoFocus
                />
              </Form.Item>
              <Row gutter={[24, 24]}>
                <Col xs={12}>
                  <Form.Item
                    label={useTranslation(
                      'partners.new.drawer.form.website.label',
                    )}
                    rules={[
                      {
                        message: <FormattedMessage id="validations.url" />,
                        pattern: URlRegEx,
                      },
                    ]}
                    name="companyUrl"
                  >
                    <Input
                      placeholder={useTranslation(
                        'partners.new.drawer.form.website.placeholder',
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item
                    label={useTranslation(
                      'partners.new.drawer.form.partner_admin.label',
                    )}
                    required
                    extra={useTranslation(
                      'partners.new.drawer.form.partner_admin.hint',
                    )}
                    name="adminEmail"
                    rules={[
                      {
                        required: true,
                        message: requiredErrorMessage,
                      },
                      {
                        validator: (_, value) => {
                          const isValid = isValidEmail(
                            (value || '').toLowerCase().trim(),
                          );

                          if (isEdit || isEmpty(value)) {
                            return Promise.resolve();
                          }

                          if (!isValid) {
                            return Promise.reject(new Error(emailErrorMessage));
                          }
                          const existingUser = users?.find(
                            (user) =>
                              user.emailId === value.trim().toLowerCase(),
                          );

                          if (existingUser != null) {
                            if (
                              existingUser.company.companyId ===
                              currentUserCompany.companyId
                            ) {
                              return Promise.reject(
                                new Error(
                                  translate(
                                    'request.invitation.errors.part_of_current_user_team',
                                  ),
                                ),
                              );
                            }

                            return Promise.reject(
                              new Error(
                                currentUserType === UserType.Native
                                  ? translate(
                                      'request.invitation.errors.part_of_another_team',
                                      {
                                        companyName:
                                          existingUser.company.companyName,
                                        type: existingUser.company.companyType,
                                      },
                                    )
                                  : translate(
                                      'request.invitation.errors.part_of_another_team.for_non_vendor',
                                    ),
                              ),
                            );
                          }

                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input
                      placeholder={useTranslation(
                        'partners.new.drawer.form.partner_admin.placeholder',
                      )}
                      disabled={isEdit}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[24, 24]}>
                <Col xs={12}>
                  <Form.Item
                    label={
                      <LabelWrapper>
                        {useTranslation(
                          'partners.new.drawer.form.max_seats_available.label',
                        )}
                        <Tooltip
                          title={
                            <FormattedMessage id="partners.new.drawer.form.max_seats_available.hint" />
                          }
                        >
                          <Information16 />
                        </Tooltip>
                      </LabelWrapper>
                    }
                    name="maxSeat"
                    rules={[
                      {
                        required: true,
                        message: requiredErrorMessage,
                      },
                      {
                        validator: (_, value) => {
                          if (value == null || value === '' || value === 0) {
                            return Promise.resolve();
                          }
                          if (value < (partner?.usedSeat ?? MIN_SEATS)) {
                            return Promise.reject(
                              new Error(
                                translate(
                                  'partners.new.drawer.form.max_seats_available.min_error',
                                ),
                              ),
                            );
                          }
                          if (
                            form.isFieldTouched('maxSeat') &&
                            isBillingEnabled &&
                            accountStatus !== AccountStatus.Trial &&
                            value > availableLicenses
                          ) {
                            return Promise.reject(
                              new Error(
                                translate(
                                  'partners.new.drawer.form.max_seats_available.max_error',
                                ),
                              ),
                            );
                          }

                          return Promise.resolve();
                        },
                      },
                    ]}
                    required
                  >
                    <InputNumber
                      min={partner?.usedSeat ?? MIN_SEATS}
                      max={
                        accountStatus === AccountStatus.Trial ||
                        !isBillingEnabled
                          ? undefined
                          : Math.max(
                              availableLicenses,
                              partner?.maxSeat ?? MIN_SEATS,
                            )
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item
                    label={useTranslation(
                      'partners.new.drawer.form.access_level.label',
                    )}
                    name="accessLevel"
                    required
                    rules={[
                      {
                        required: true,
                        message: requiredErrorMessage,
                      },
                    ]}
                  >
                    <Select
                      options={accessLevelOptions}
                      isLoading={isPartnerPermissionsLoading}
                      components={{ Option: DescriptiveOption }}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col xs={24}>
                  <Form.Item name="maskPartner" valuePropName="checked">
                    <Checkbox>
                      <CentreAlignWrapper>
                        <span>
                          <FormattedMessage id="partners.new.drawer.form.strategic_partner.label" />
                        </span>
                        <Tooltip
                          title={
                            <FormattedMessage
                              id="partners.new.drawer.form.strategic_partner.hint"
                              values={{
                                br: () => (
                                  <>
                                    <br />
                                    <br />
                                  </>
                                ),
                              }}
                            />
                          }
                        >
                          <Information16 />
                        </Tooltip>
                      </CentreAlignWrapper>
                    </Checkbox>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </FormWrapper>
        </CustomLayoutDrawerContent.Content>
        <CustomLayoutDrawerContent.Footer>
          <Footer>
            <Button type="tertiary" onClick={onClose}>
              <FormattedMessage id="cancel" />
            </Button>
            {isEdit ? (
              <Button
                type="primary"
                icon={<Save16 />}
                iconPlacement="right"
                onClick={submitForm}
                loading={isUpdating}
              >
                <FormattedMessage id="save" />
              </Button>
            ) : (
              <Button
                type="primary"
                icon={<ArrowRight16 />}
                iconPlacement="right"
                onClick={submitForm}
                loading={isCreating}
              >
                <FormattedMessage id="partners.new.drawer.actions.create_partner" />
              </Button>
            )}
          </Footer>
        </CustomLayoutDrawerContent.Footer>
      </DrawerContent>
    </CustomLayoutDrawer>
  );
};
